/** Connects an ImageDocument to React: re-renders on change, throttles previews to animation frames and reads pixels from the loaded image. */
import {useCallback, useState} from 'react';
import {ImageDocument} from './imageDocument';
import {PixelBuffer} from './imageEditing';

export interface ImageDocumentHandle {
   imageDocument: ImageDocument;
   /** True when the image could not be loaded or read, so there is nothing to edit or export. */
   loadFailed: boolean;
   loadImage: (image: HTMLImageElement) => void;
   failLoad: () => void;
}

/** Reads an image's natural-size RGBA pixels through an offscreen canvas; throws when they cannot be read. */
const readPixels = (image: HTMLImageElement): PixelBuffer => {
   const canvas = document.createElement('canvas');
   canvas.width = image.naturalWidth;
   canvas.height = image.naturalHeight;
   const context = canvas.getContext('2d');
   if (!context) {
      throw new Error('Canvas 2D context is unavailable');
   }
   context.drawImage(image, 0, 0);
   return context.getImageData(0, 0, canvas.width, canvas.height);
};

/** Owns one ImageDocument per editor and re-renders the editor whenever the document changes. */
export const useImageDocument = (): ImageDocumentHandle => {
   const [, setRevision] = useState(0);
   const [loadFailed, setLoadFailed] = useState(false);
   const [imageDocument] = useState(() => new ImageDocument(
      () => setRevision(revision => revision + 1),
      callback => {
         window.requestAnimationFrame(callback);
      }
   ));

   const loadImage = useCallback((image: HTMLImageElement) => {
      try {
         imageDocument.load(readPixels(image));
         setLoadFailed(false);
      } catch {
         setLoadFailed(true);
      }
   }, [imageDocument]);

   const failLoad = useCallback(() => setLoadFailed(true), []);

   return {imageDocument, loadFailed, loadImage, failLoad};
};
